import { applyThresholdDefaults } from './apply-threshold-defaults.js';
import { resolveShowThresholdIndicators } from './resolve-show-threshold-indicators.js';
import { isValidColorRule } from '../../../core/slots/ColorRule.js';
import { DEFAULT_FORMATTER } from '../../shared/constants.js';
import {
  defaultToolbarInternalConfig,
  MeterBarToolbarConfig,
  MeterBarToolbarInternalProps,
  MeterBarToolbarSlotProps,
} from '../../shared/types/meter-bar-toolbar.js';
import { defaultConfig } from '../types/default.js';
import {
  MeterBarChartConsumerConfig,
  MeterBarChartSlots,
  type MeterBarChartThresholdConsumerConfig,
} from '../types/meter-bar-chart-config.js';
import {
  MeterBarChartInternalConfig,
  type ThresholdMetadata,
  type TooltipConfigData,
} from '../types/meter-bar-chart-internal.js';
import { MeterBarChartProps } from '../types/meter-bar-chart.js';

const getTooltipConfig = (
  slots: MeterBarChartSlots,
  consumerConfig: MeterBarChartConsumerConfig,
): TooltipConfigData => {
  let consumerTooltip: { hidden: boolean } | undefined;
  if (typeof consumerConfig.tooltip === 'boolean') {
    // TODO [APPDEV-17990] Remove the boolean branch.
    consumerTooltip = { hidden: !consumerConfig.tooltip };
  } else if (
    typeof consumerConfig.tooltip === 'object' &&
    consumerConfig.tooltip.hidden !== undefined
  ) {
    consumerTooltip = { hidden: consumerConfig.tooltip.hidden };
  }
  return { ...defaultConfig.tooltip, ...consumerTooltip, ...slots.tooltip };
};

const getToolbarConfig = (
  toolbarSlotsConfig?: MeterBarToolbarSlotProps,
  toolbarConsumerConfig?: MeterBarToolbarConfig,
): MeterBarToolbarInternalProps => {
  const configuredToolbar = toolbarSlotsConfig ?? toolbarConsumerConfig;

  return { ...defaultToolbarInternalConfig, ...configuredToolbar };
};

export const buildMeterBarChartConfig = (
  props: MeterBarChartProps,
  consumerConfig: MeterBarChartConsumerConfig,
  slots: MeterBarChartSlots,
): MeterBarChartInternalConfig => {
  const thresholds = combineThresholds(
    consumerConfig.thresholdsIndicators,
    slots.thresholds,
  );
  const size = props.size ?? consumerConfig.size ?? defaultConfig.size;
  const min = props.min ?? consumerConfig.min ?? defaultConfig.min;
  const max = props.max ?? consumerConfig.max ?? defaultConfig.max;
  const tooltip = getTooltipConfig(slots, consumerConfig);

  const formatter =
    props.formatter ?? consumerConfig.formatter ?? DEFAULT_FORMATTER;
  const unit = props.unit ?? consumerConfig.unit;

  const toolbar = getToolbarConfig(slots.toolbar, consumerConfig.toolbar);

  const colorRules = (consumerConfig.colorRules ?? [])
    .filter(isValidColorRule)
    .concat(slots.colorRules ?? []);

  const loading =
    props.loading ?? consumerConfig.loading ?? defaultConfig.loading;

  const showThresholdIndicators =
    props.showThresholdIndicators ??
    resolveShowThresholdIndicators(thresholds.data) ??
    defaultConfig.showThresholdIndicators;

  return {
    thresholds,
    thresholdsIndicators: thresholds,
    showThresholdIndicators,
    size,
    loading,
    min,
    max,
    tooltip,
    labelSlot: slots.label,
    valueSlot: slots.value,
    minSlot: slots.min,
    maxSlot: slots.max,
    formatter,
    unit,
    colorRules,
    toolbar,
  };
};

const combineThresholds = (
  thresholdConfig: MeterBarChartThresholdConsumerConfig | undefined,
  thresholdSlots: ThresholdMetadata | undefined,
): ThresholdMetadata => {
  const slotsData = thresholdSlots?.data ? thresholdSlots.data : [];
  const consumerData = thresholdConfig?.data?.map(applyThresholdDefaults) ?? [];

  return {
    data: slotsData.concat(consumerData),
    legend:
      thresholdSlots?.legend ??
      thresholdConfig?.legend ??
      defaultConfig.thresholdIndicators.legend,
  };
};
